'use client';

import { useCallback } from 'react';
import {
  normalizeOptionsObj,
  useUpdateURL
} from '../utils/product-context-helpers';
import { areValidOptions } from '../utils/product-options';
import { remapAdditionalProducts } from '../utils/additional-products';
import { useProduct } from './use-product';
import { useSelectionStore } from './private/use-selection-store';
import { useResolvedOptions } from './private/use-resolved-options';

/**
 * Actions for updating the selected product options and quantity.
 *
 * Requires <Suspense>.
 */
export function useSelectionActions() {
  const { productData, crossSellProducts } = useProduct();
  const { selectedOptions } = useResolvedOptions();
  const { setSelectedOptionsOverride, setAdditionalProducts, setQuantity } =
    useSelectionStore();
  const updateURL = useUpdateURL();
  const shopifyProduct = productData.shopifyProduct;

  const updateOption = useCallback(
    (newOptions) => {
      const normalized = normalizeOptionsObj(newOptions);

      if (!areValidOptions(shopifyProduct, normalized)) {
        console.error('Invalid options:', newOptions);
        return;
      }

      setSelectedOptionsOverride({ ...selectedOptions, ...normalized });

      // Option changes can invalidate selected cross-sells.
      setAdditionalProducts((additionalProducts) =>
        remapAdditionalProducts({
          changedOptions: normalized,
          previousOptions: selectedOptions,
          additionalProducts,
          crossSellProducts
        })
      );

      updateURL(normalized);
    },
    [
      shopifyProduct,
      selectedOptions,
      crossSellProducts,
      setSelectedOptionsOverride,
      setAdditionalProducts,
      updateURL
    ]
  );

  const updateQuantity = useCallback(
    (qty) => setQuantity(Math.max(1, qty)), // clamp at 1
    [setQuantity]
  );

  return { updateOption, updateQuantity };
}
